import { useIntl } from 'react-intl';
import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router-dom';
import { Avatar, Card, InfoBox, StatusBadge } from '@immo/ui-kit';
import { MESSENGER_CHAT_MESSAGE_CAP, type GetMessengerChatResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { requireUuidParam } from '@/lib/routing/require-uuid-param';
import { memberInitials } from '@/lib/member-initials';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ChatIcon } from '@/components/layout/icons';

const DASH = '—';

export async function messengerChatDetailLoader({ params }: LoaderFunctionArgs): Promise<GetMessengerChatResponse> {
  const chatId = requireUuidParam(params, 'chatId');

  const result = await apiClient.adminMessengerChats.get({ pathParams: { chatId } });

  if (!result.ok) {
    throw new Response('Failed to load the messenger chat', { status: result.status });
  }

  return result.body;
}

export function MessengerChatDetailPage() {
  const t = useTranslations('pages.admin.messenger-chats');
  const tDetail = useTranslations('pages.admin.messenger-chats.detail');
  const intl = useIntl();
  const chat = useLoaderData() as GetMessengerChatResponse;

  const title = chat.member.name ?? chat.account;

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={ChatIcon}
            crumbs={[{ label: t('title'), to: '/messenger/chats' }, { label: title }]}
          />
        }
        trailing={<StatusBadge tone="warning" size="sm" label={t('experimental')} />}
      />
      <div className="space-y-4">
        <Link to="/messenger/chats" className="inline-block text-text-tertiary hover:text-text-primary hover:underline">
          ← {tDetail('back')}
        </Link>
        <div className="grid grid-cols-[minmax(0,1fr)_300px] gap-4">
          <Card className="min-w-0 space-y-4 p-4">
            {chat.truncated && (
              <p className="text-sm text-text-tertiary">
                {tDetail('truncated', { limit: MESSENGER_CHAT_MESSAGE_CAP })}
              </p>
            )}
            <div className="space-y-3">
              {chat.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  direction={message.direction}
                  body={message.body}
                  occurredAt={message.occurredAt}
                  senderLabel={
                    message.direction === 'OUTBOUND' ? tDetail('immoos') : (chat.member.name ?? chat.account)
                  }
                  intl={intl}
                />
              ))}
            </div>
            <InfoBox type="info">{tDetail('read-only')}</InfoBox>
          </Card>
          <Card className="min-w-0 self-start space-y-3 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">
              {tDetail('member-panel')}
            </h2>
            <div className="flex min-w-0 items-center gap-2">
              {chat.member.name !== null && <Avatar initials={memberInitials(chat.member.name)} size="sm" />}
              <span className="truncate text-text-primary">{chat.member.name ?? DASH}</span>
            </div>
            <MemberField label={tDetail('email')} value={chat.member.email} className="break-words" />
            <MemberField label={tDetail('workspace')} value={chat.workspace.name} />
            <MemberField label={tDetail('channel')} value={t(`channels.${chat.messenger}`)} />
            <MemberField label={tDetail('account')} value={chat.account} className="whitespace-nowrap" />
          </Card>
        </div>
      </div>
    </>
  );
}

function MemberField({
  label,
  value,
  className,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly className?: string;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-sm text-text-tertiary">{label}</div>
      <div className={value === null ? 'text-text-placeholder' : className}>{value ?? DASH}</div>
    </div>
  );
}

function MessageBubble({
  direction,
  body,
  occurredAt,
  senderLabel,
  intl,
}: {
  readonly direction: 'INBOUND' | 'OUTBOUND';
  readonly body: string;
  readonly occurredAt: string;
  readonly senderLabel: string;
  readonly intl: ReturnType<typeof useIntl>;
}) {
  const outbound = direction === 'OUTBOUND';

  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div className="min-w-0 max-w-[78%] space-y-1">
        <div className="text-xs text-text-tertiary">
          {senderLabel} · {intl.formatDate(occurredAt, { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
        <div
          className={`min-w-0 rounded-md px-3 py-2 whitespace-pre-wrap break-words text-base-sm ${
            outbound ? 'bg-interactive-subtle-bg' : 'border border-neutral-200 bg-surface'
          }`}
        >
          {body}
        </div>
      </div>
    </div>
  );
}
